import { Injectable, signal } from '@angular/core';
import { Subject } from 'rxjs';
import { AiAction, FieldChange, parseActionsFromText } from './ai-action.types';
import { LocalStorageService } from '../local-storage-service/local-storage.service';
import { SemToken } from '../../models/oauth/semToken';
import { environment } from '../../../environments/environment';

export interface AiMessage {
  role: 'user' | 'assistant';
  content: string;
  actions?: AiAction[];
  timestamp: Date;
}

@Injectable({ providedIn: 'root' })
export class AiPanelService {
  isOpen = signal(false);
  messages = signal<AiMessage[]>([]);
  isLoading = signal(false);

  // Current record context — set by sem-dynamic when a record is loaded
  currentFormName = signal<string | null>(null);
  currentRecordId = signal<number | null>(null);
  currentRecordData = signal<any>(null);

  // Event stream for applying field changes
  private fieldChangesSubject = new Subject<FieldChange[]>();
  fieldChangesApplied$ = this.fieldChangesSubject.asObservable();

  // Feedback signal for apply result
  lastApplyResult = signal<{ success: boolean; count: number } | null>(null);

  private abortController: AbortController | null = null;

  constructor(private localStorageService: LocalStorageService) {}

  private getToken(): string {
    const stToken = this.localStorageService.get('token');
    if (stToken != null) {
      try {
        const semToken: SemToken = Object.assign(new SemToken(), JSON.parse(stToken));
        if (new Date(semToken.expires) > new Date()) {
          return semToken.accessToken;
        }
      } catch { }
    }
    return '';
  }

  toggle() {
    this.isOpen.update(v => !v);
  }

  open() {
    this.isOpen.set(true);
  }

  close() {
    this.isOpen.set(false);
  }

  setRecordContext(formName: string | null, recordId: number | null, recordData: any = null) {
    this.currentFormName.set(formName);
    this.currentRecordId.set(recordId);
    this.currentRecordData.set(recordData);
  }

  applyFieldChanges(changes: FieldChange[]) {
    const selected = changes.filter(c => c.selected);
    if (selected.length === 0) return;
    this.fieldChangesSubject.next(selected);
    this.lastApplyResult.set({ success: true, count: selected.length });
    setTimeout(() => this.lastApplyResult.set(null), 3000);
  }

  async sendMessage(message: string) {
    const userMsg: AiMessage = { role: 'user', content: message, timestamp: new Date() };
    this.messages.update(msgs => [...msgs, userMsg]);
    this.isLoading.set(true);

    const assistantMsg: AiMessage = { role: 'assistant', content: '', timestamp: new Date() };
    this.messages.update(msgs => [...msgs, assistantMsg]);

    try {
      this.abortController = new AbortController();

      const body: any = {
        messages: this.messages()
          .filter(m => m !== assistantMsg)
          .map(m => ({ role: m.role, content: m.content })),
        formName: this.currentFormName(),
        recordId: this.currentRecordId(),
        recordData: this.currentRecordData()
      };

      const token = this.getToken();
      const headers: Record<string, string> = {
        'Content-Type': 'application/json'
      };
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      const response = await fetch(`${environment.wgProfileHost}/api/ai/chat`, {
        method: 'POST',
        headers,
        body: JSON.stringify(body),
        signal: this.abortController.signal
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const reader = response.body!.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let fullContent = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
          if (!line.startsWith('data: ')) continue;
          const data = line.slice(6).trim();
          if (data === '[DONE]') break;

          try {
            const parsed = JSON.parse(data);

            // Handle structured actions event from backend
            if (parsed.actions && Array.isArray(parsed.actions)) {
              for (const action of parsed.actions) {
                if (action.type === 'field-changes' && Array.isArray(action.changes)) {
                  for (const change of action.changes) {
                    if (change.selected === undefined) change.selected = true;
                  }
                }
              }
              this.messages.update(msgs => {
                const updated = [...msgs];
                const last = updated[updated.length - 1];
                if (last.role === 'assistant') {
                  updated[updated.length - 1] = { ...last, actions: parsed.actions };
                }
                return updated;
              });
              continue;
            }

            if (parsed.content) {
              fullContent += parsed.content;
              const { text, actions } = parseActionsFromText(fullContent);

              this.messages.update(msgs => {
                const updated = [...msgs];
                const last = updated[updated.length - 1];
                if (last.role === 'assistant') {
                  updated[updated.length - 1] = {
                    ...last,
                    content: text,
                    ...(actions.length > 0 ? { actions } : {})
                  };
                }
                return updated;
              });
            }
          } catch { /* ignore malformed chunks */ }
        }
      }

      // Final parse pass
      if (fullContent) {
        const { text, actions } = parseActionsFromText(fullContent);
        if (actions.length > 0) {
          this.messages.update(msgs => {
            const updated = [...msgs];
            const last = updated[updated.length - 1];
            if (last.role === 'assistant') {
              updated[updated.length - 1] = { ...last, content: text, actions };
            }
            return updated;
          });
        }
      }
    } catch (err: any) {
      if (err.name !== 'AbortError') {
        this.messages.update(msgs => {
          const updated = [...msgs];
          const last = updated[updated.length - 1];
          if (last.role === 'assistant' && !last.content) {
            updated[updated.length - 1] = { ...last, content: 'Prišlo je do napake. Poskusite znova.' };
          }
          return updated;
        });
        console.error('AI chat error:', err);
      }
    } finally {
      this.isLoading.set(false);
      this.abortController = null;
    }
  }

  cancelRequest() {
    this.abortController?.abort();
  }

  clearChat() {
    this.cancelRequest();
    this.messages.set([]);
    this.isLoading.set(false);
    this.lastApplyResult.set(null);
  }
}
